import React, { ReactNode, ReactElement } from "react";
import { PayloadAction } from "redux-starter-kit";
import { InputProps } from "@amzn/awsui-components-react-v3";
import { FieldValidationRules } from "@amzn/crowd-front-end-common-library";
import { actions } from "../formSlice";

const isIntegerRegex = /^\d$/;

/**
 * Interface for FormFieldInputWrapper properties.
 *
 * @interface FormFieldInputWrapperProps
 */
interface FormFieldInputWrapperProps {
  children: ReactNode;
  propertyName: string;
  value?: string | number;
  dispatch: (action: PayloadAction<unknown>) => void;
  debounceTime?: number;
  dispatchOnValueUpdate: boolean;
  validationRules?: FieldValidationRules;
}

/**
 * Component that wraps an input element. The purpose is to debounce input.
 *
 * @param {FormFieldInputWrapperProps} {
 *   children,
 *   propertyName,
 *   value,
 *   dispatch,
 * }
 * @returns {JSX.Element}
 */
const FormFieldInputWrapper: React.FC<FormFieldInputWrapperProps> = ({
  children,
  propertyName,
  value,
  dispatch,
  validationRules,
}: FormFieldInputWrapperProps): JSX.Element => {
  const setInputValue = (input: string): void => {
    dispatch(
      actions.setPropertyValueAction({
        propertyName,
        value: input,
      })
    );
  };

  const onKeyDown = (event: CustomEvent<InputProps.KeyDetail>): void => {
    if (
      validationRules &&
      validationRules.numericality &&
      (validationRules.numericality as { onlyInteger?: boolean }).onlyInteger &&
      !isIntegerRegex.test(event.detail.key)
    ) {
      event.preventDefault();
    }
  };

  // TODO fix debouncing mechanism
  const inputProps: InputProps = {
    onChange: (event) => setInputValue(event.detail.value),
    value: value ? value.toString() : "",
    onKeyDown,
  };

  return React.Children.only(
    React.cloneElement(children as ReactElement<unknown>, inputProps)
  );
};

export default FormFieldInputWrapper;
